export type DiffSegment = { type: 'equal' | 'added' | 'removed'; text: string }

// CJK Unicode ranges: CJK Unified Ideographs, Hiragana, Katakana, CJK symbols,
// fullwidth forms, CJK compatibility ideographs, Hangul syllables
const CJK =
  '\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uF900-\uFAFF\uFF00-\uFFEF\uAC00-\uD7AF'

/**
 * Tokenize text into diffable units.
 * Uses Intl.Segmenter (word-level) when available for proper Japanese/Chinese
 * word boundaries. Falls back to a regex that splits CJK characters individually
 * while keeping Latin words grouped.
 */
function tokenize(text: string = ''): string[] {
  const safeText = text ?? ''
  if (!safeText) return []
  // Use Intl.Segmenter for proper word-level segmentation when available
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const IntlAny = Intl as any
  if (typeof IntlAny.Segmenter === 'function') {
    const segmenter = new IntlAny.Segmenter(undefined, { granularity: 'word' })
    return Array.from(
      segmenter.segment(safeText) as Iterable<{ segment: string }>,
      (s: { segment: string }) => s.segment
    )
  }
  // Fallback: each CJK char is its own token, whitespace and Latin words grouped
  const re = new RegExp(`\\s+|[${CJK}]|[^\\s${CJK}]+`, 'g')
  return safeText.match(re) || []
}

export function computeWordDiff(oldText: string = '', newText: string = ''): DiffSegment[] {
  const oldWords = tokenize(oldText ?? '')
  const newWords = tokenize(newText ?? '')

  const m = oldWords.length
  const n = newWords.length

  // LCS of suffixes: dp[i][j] = LCS length of oldWords[i:] and newWords[j:]
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0))
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (oldWords[i] === newWords[j]) {
        dp[i][j] = dp[i + 1][j + 1] + 1
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1])
      }
    }
  }

  // Walk forward so tokens match at their earliest position. Walking back from
  // the end matched them at their last one: an unchanged sentence followed by
  // an addition that repeats its words came out as scattered changes.
  const segments: DiffSegment[] = []
  let i = 0
  let j = 0
  const raw: DiffSegment[] = []

  while (i < m || j < n) {
    if (i < m && j < n && oldWords[i] === newWords[j]) {
      raw.push({ type: 'equal', text: oldWords[i] })
      i++
      j++
    } else if (i < m && (j === n || dp[i + 1][j] >= dp[i][j + 1])) {
      raw.push({ type: 'removed', text: oldWords[i] })
      i++
    } else {
      raw.push({ type: 'added', text: newWords[j] })
      j++
    }
  }

  // Merge consecutive segments of the same type
  for (const seg of raw) {
    const last = segments[segments.length - 1]
    if (last && last.type === seg.type) {
      last.text += seg.text
    } else {
      segments.push({ ...seg })
    }
  }

  return segments
}
